import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { INVOICE_KEYS } from '@app/app.constants';
import { FollowUpType } from '@app/modules/follow-ups/follow-ups.types';
import { ReportsUtilsService } from '@app/modules/reports/services/reports-utils.service';
import { GetReportsListAction } from '@app/modules/reports/store/reports.actions';
import { ReportsState } from '@app/modules/reports/store/reports.state';
import {
  isLowRiskReport,
  Report,
  ReportStatus,
  ReportType,
} from '@app/modules/reports/types/report.types';
import {
  ExtendedProductInventoryItem,
  ProductInventoryItemStatus,
} from '@app/modules/service-data/types/service-data.types';
import { PaymentType } from '@core/core.types';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { LoadInvoicesPaymentsListAction } from '@core/store/payments/invoice-payments.actions';
import { InvoicesState } from '@core/store/payments/invoice-payments.state';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ConfirmService } from '@shared/components/confirm/confirm-dialog.component';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableDataType,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { DocumentInteractionsListComponent } from '@shared/components/document-interactions-list/document-interactions-list.component';
import { ConfirmCloseReason } from '@shared/shared.types';
import { Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-reports-list-section',
  imports: [DataTableComponent, DocumentInteractionsListComponent],
  templateUrl: './reports-list-section.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reports-list-section.component.scss',
})
export class ReportsListSectionComponent implements OnInit {
  @ViewChild('interactionTableTemplate', { static: true })
  interactionTableTemplate: TemplateRef<any>;

  private confirmService = inject(ConfirmService);
  private translateService = inject(TranslateService);
  private store = inject(Store);
  protected reportsUtils = inject(ReportsUtilsService);
  private authService = inject(AccountUtilsService);

  reportIds = input<string[]>(null);
  pii = input<ExtendedProductInventoryItem>(null);

  reports = computed(() =>
    this.authService.hasRequiredPermission(
      'pii.details.documentsList.showUnapproved'
    )
      ? this.store.selectSignal(ReportsState.getReportsByIds)()(
          this.reportIds()
        )
      : this.store
          .selectSignal(ReportsState.getReportsByIds)()(this.reportIds())
          .filter((report) => report.status === ReportStatus.Approved)
  );

  reports$ = toObservable(this.reports);

  protected dataTableConfiguration: DataTableConfiguration;

  ngOnInit() {
    this.store.dispatch(
      new GetReportsListAction({ ids: this.reportIds() }, {}, null, true)
    );

    this.dataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.SINGLE,
      tableHeadClasses: '',
      tableCellClasses: 'align-middle',
      actionsClasses: 'w-15 text-end',
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'reports.list.actions.approveReport',
          action: (e, report) =>
            this.shouldApprove().subscribe((approve) => {
              if (approve) {
                this.reportsUtils.approveUnofficialReport(report);
              }
            }),
          type: DataTableActionType.BUTTON,
          disabled: (report) =>
            report.status == ReportStatus.Approved ||
            report.status == ReportStatus.Invalid ||
            this.reportsUtils.reportActionInProgress(),
          invisible: (report: Report) =>
            report.reportType === ReportType.Official &&
            isLowRiskReport(report.reportedRisk),
          icon: 'fa-light fa-check-circle',
          permission: 'pii.details.documents',
        },
        {
          name: 'reports.list.actions.approveReport',
          action: (e, report) =>
            this.shouldApprove().subscribe((approve) => {
              if (approve) {
                this.reportsUtils.massApproveReports([report]);
              }
            }),
          type: DataTableActionType.BUTTON,
          disabled: (report) =>
            report.status == ReportStatus.Approved ||
            report.status == ReportStatus.Invalid,
          invisible: (report) =>
            report.reportType !== ReportType.Official ||
            !isLowRiskReport(report.reportedRisk),
          icon: 'fa-light fa-check-circle',
          permission: 'pii.details.documents',
        },
        {
          name: 'reports.list.actions.disapproveReport',
          action: (e, report) => this.reportsUtils.disapproveReport(report),
          type: DataTableActionType.BUTTON,
          disabled: (report) =>
            this.reportsUtils.reportActionInProgress() ||
            report.status == ReportStatus.Generated ||
            report.status == ReportStatus.Invalid ||
            (this.pii().lastStatus ===
              ProductInventoryItemStatus.processingFailed &&
              !this.authService.hasRequiredPermission(
                'pii.details.documentsList.disapprove.enabledAfterApproved'
              )),
          icon: 'fa-light fa-xmark',
          permission: [
            'pii.details.documents',
            'pii.details.documentsList.disapprove',
          ],
        },
        {
          name: 'reports.list.actions.viewReport',
          action: (e, report) => this.reportsUtils.viewReport(report.id),
          type: DataTableActionType.BUTTON,
          disabled: (report) => report.status == ReportStatus.Invalid,
          icon: 'fa-light fa-eye',
          permission: 'pii.details',
        },
        {
          name: 'reports.list.actions.archiveReport',
          action: (e, report) => this.reportsUtils.archiveReports([report]),
          type: DataTableActionType.BUTTON,
          disabled: (report) =>
            report.status == ReportStatus.Archived ||
            report.status == ReportStatus.Approved,
          icon: 'fa-light fa-archive',
          permission: 'pii.details.documents',
        },
        {
          name: 'reports.list.actions.removeReport',
          action: (e, report) => this.reportsUtils.removeReport(report),
          type: DataTableActionType.BUTTON,
          disabled: (report) =>
            report.status == ReportStatus.Invalid || report.nextReportId,
          icon: 'fa-light fa-trash',
          permission: 'pii.details.documents.buttons.delete',
        },
      ],
      columns: [
        {
          label: 'reports.list.columns.kitId',
          invoke: {
            objectReference: {
              fn: (report: any) =>
                report.properties?.find((p) => p.key === 'KitId')?.value,
            },
            name: 'fn',
            args: [],
          },
          customStyle: 'width: 17%',
        },
        {
          label: 'reports.list.columns.reportType',
          property: 'reportType',
          customStyle: 'width: 17%',
        },
        {
          label: 'reports.list.columns.reportReason',
          property: 'reportReason',
          customStyle: 'width: 19%',
        },
        {
          label: 'reports.list.columns.generated',
          property: 'generated',
          type: DataTableDataType.DATETIME,
          customStyle: 'width: 17%',
        },
        {
          label: 'reports.list.columns.status',
          property: 'status',
        },
      ],
      extraRow: this.interactionTableTemplate,
      rows$: this.reports$,
    };
  }

  shouldApprove() {
    return this.invoiceAndPaymentRowsExist().pipe(
      first(),
      switchMap((exists: boolean) => this.handlePayment(exists)),
      switchMap((approve) => this.handleFollowUps(approve))
    );
  }

  handlePayment(exists: boolean) {
    {
      if (
        this.pii().sample.serviceData.paymentInformation.paymentType !==
          PaymentType.Partner &&
        !this.pii().sample.serviceData.paymentInformation.paid &&
        !exists
      ) {
        return this.confirmService
          .confirm({
            title: this.translateService.instant(
              'pii.details.approve.areYouSureTitle'
            ),
            message: this.translateService.instant(
              'pii.details.approve.areYouSureMessage'
            ),
          })
          .pipe(
            first(),
            switchMap((confirmOpts) => {
              if (confirmOpts.closeReason === ConfirmCloseReason.No) {
                return of(false);
              }

              return of(true);
            })
          );
      }

      return of(true);
    }
  }

  handleFollowUps(approve) {
    if (!approve) {
      return of(false);
    }
    if (
      this.pii().sample.followUps.some(
        (followUp) =>
          followUp.type === FollowUpType.ConsentIssue &&
          !followUp.resolved &&
          !followUp.archived
      )
    ) {
      return this.confirmService
        .confirm({
          title: this.translateService.instant(
            'pii.details.approve.areYouSureTitleFollowUps'
          ),
          message: this.translateService.instant(
            'pii.details.approve.areYouSureMessageFollowUps'
          ),
        })
        .pipe(
          first(),
          switchMap((confirmOpts) => {
            if (confirmOpts.closeReason === ConfirmCloseReason.No) {
              return of(false);
            }

            return of(true);
          })
        );
    }
    return of(true);
  }

  private invoiceAndPaymentRowsExist(): Observable<boolean> {
    const invoice = this.pii().sample.serviceData.invoice;

    if (invoice?.properties) {
      const property = invoice.properties.length
        ? invoice.properties.find(
            (p) =>
              p.itemKey.toLocaleLowerCase() ===
              INVOICE_KEYS.SepaContract.toLocaleLowerCase()
          )
        : null;

      if (JSON.parse(property.itemValue))
        return this.store
          .dispatch(new LoadInvoicesPaymentsListAction(invoice.id))
          .pipe(
            first(),
            switchMap(() =>
              this.store
                .select(InvoicesState.getInvoicePymentsByInvoiceId)
                .pipe(
                  map((getInvoicePaymentsById) =>
                    getInvoicePaymentsById(invoice.id)
                  ),
                  switchMap((invoicePayments) => {
                    if (
                      !invoicePayments ||
                      !invoicePayments.length ||
                      (invoicePayments.length && !invoicePayments[0].paid)
                    ) {
                      return of(false);
                    }
                    return of(true);
                  })
                )
            )
          );
    }

    return of(false);
  }
}
